import React, { useState, useMemo } from 'react';
import type { Metric } from '@lnr-v2/types';
import { AdminLayout } from '../../layouts/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  useMetrics,
  useCreateMetric,
  useUpdateMetric,
  useDeleteMetric,
} from '../../hooks/useMetrics';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { toast } from '../../utils/toast';
import styles from './MetricsManager.module.css';

export const MetricsManager: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

  // State for form and filters
  const [showForm, setShowForm] = useState(false);
  const [editingMetric, setEditingMetric] = useState<Metric | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Form state - simplified based on current schema
  const [formData, setFormData] = useState({
    value: 0,
    label: '',
    description: '',
    sortOrder: 0,
    isPublic: false,
  });

  // Build filters object
  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    return f;
  }, []);

  // Hooks
  const { metrics, loading, error, refetch } = useMetrics(filters);
  const { createMetric, loading: creating } = useCreateMetric();
  const { updateMetric, loading: updating } = useUpdateMetric();
  const { deleteMetric, loading: deleting } = useDeleteMetric();

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.label.trim()) {
      toast.error('Label is required');
      return;
    }

    try {
      if (editingMetric) {
        await updateMetric(editingMetric.id, formData);
        toast.success('Metric updated successfully');
      } else {
        await createMetric(formData);
        toast.success('Metric created successfully');
      }

      // Reset form
      setFormData({
        value: 0,
        label: '',
        description: '',
        sortOrder: 0,
        isPublic: false,
      });
      setShowForm(false);
      setEditingMetric(null);
      refetch();
    } catch (error) {
      const err = error as Error;
      console.error('Failed to save metric:', err);
      toast.error(
        err.message ||
          (editingMetric
            ? 'Failed to update metric'
            : 'Failed to create metric')
      );
    }
  };

  // Handle delete
  const handleDelete = async (metricId: string) => {
    if (!window.confirm('Are you sure you want to delete this metric?')) {
      return;
    }

    setDeletingId(metricId);
    try {
      await deleteMetric(metricId);
      toast.success('Metric deleted successfully');
      refetch();
    } catch (error) {
      const err = error as Error;
      console.error('Failed to delete metric:', err);
      toast.error(err.message || 'Failed to delete metric');
    } finally {
      setDeletingId(null);
    }
  };

  // Handle edit
  const handleEdit = (metric: Metric) => {
    setEditingMetric(metric);
    setFormData({
      value: Number(metric.value),
      label: metric.label,
      description: metric.description || '',
      sortOrder: metric.sortOrder,
      isPublic: metric.isPublic,
    });
    setShowForm(true);
  };

  // Loading state
  if (loading && !metrics) {
    return (
      <AdminLayout>
        <LoadingOverlay isLoading={true} />
      </AdminLayout>
    );
  }

  // Error state
  if (error && !metrics) {
    return (
      <AdminLayout>
        <div className={styles.container}>
          <div className={styles.errorContainer}>
            <div className={styles.errorContent}>
              <h3 className={styles.errorTitle}>Unable to Load Metrics</h3>
              <p className={styles.errorMessage}>{error.message}</p>
              <button
                onClick={() => refetch()}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>Metrics</h1>
            <p className={styles.subtitle}>Manage your business metrics</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => {
                setShowForm(!showForm);
                if (!showForm) {
                  setEditingMetric(null);
                  setFormData({
                    value: 0,
                    label: '',
                    description: '',
                    sortOrder: 0,
                    isPublic: false,
                  });
                }
              }}
              className={`${styles.button} ${styles.buttonPrimary}`}
            >
              {showForm ? 'Cancel' : 'Add Metric'}
            </button>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <div className={styles.formContainer}>
            <h2 className={styles.formTitle}>
              {editingMetric ? 'Edit Metric' : 'Create New Metric'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGrid}>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Label</label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) =>
                      setFormData({ ...formData, label: e.target.value })
                    }
                    className={styles.formInput}
                    disabled={creating || updating}
                    required
                  />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Value</label>
                  <input
                    type="number"
                    value={formData.value}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        value: parseFloat(e.target.value) || 0,
                      })
                    }
                    className={styles.formInput}
                    disabled={creating || updating}
                    required
                  />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Sort Order</label>
                  <input
                    type="number"
                    value={formData.sortOrder || 0}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sortOrder: parseInt(e.target.value) || 0,
                      })
                    }
                    className={styles.formInput}
                    disabled={creating || updating}
                  />
                </div>
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Description</label>
                <input
                  type="text"
                  value={formData.description || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className={styles.formInput}
                  placeholder="Optional description"
                  disabled={creating || updating}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={(e) =>
                      setFormData({ ...formData, isPublic: e.target.checked })
                    }
                    disabled={creating || updating}
                  />
                  Public (visible on website)
                </label>
              </div>
              <div className={styles.formActions}>
                <button
                  type="submit"
                  disabled={creating || updating}
                  className={`${styles.button} ${styles.buttonPrimary}`}
                >
                  {creating || updating
                    ? 'Saving...'
                    : editingMetric
                      ? 'Update'
                      : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingMetric(null);
                    setFormData({
                      value: 0,
                      label: '',
                      description: '',
                      sortOrder: 0,
                      isPublic: false,
                    });
                  }}
                  disabled={creating || updating}
                  className={`${styles.button} ${styles.buttonSecondary}`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Metrics Grid */}
        <div className={styles.metricsGrid}>
          {!loading && metrics?.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyStateText}>No metrics found</p>
            </div>
          ) : (
            metrics?.map((metric) => (
              <div key={metric.id} className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricLabel}>{metric.label}</h3>
                  {metric.isPublic && (
                    <span className={`${styles.badge} ${styles.badgePublic}`}>
                      Public
                    </span>
                  )}
                </div>
                <div className={styles.metricValue}>
                  <span className={styles.value}>
                    {Number(metric.value).toLocaleString()}
                  </span>
                </div>
                {metric.description && (
                  <p className={styles.metricDescription}>
                    {metric.description}
                  </p>
                )}
                {isAdmin && (
                  <div className={styles.metricActions}>
                    <button
                      onClick={() => handleEdit(metric)}
                      className={`${styles.button} ${styles.buttonSecondary}`}
                      disabled={deleting}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(metric.id)}
                      disabled={deletingId === metric.id || deleting}
                      className={`${styles.button} ${styles.buttonDanger}`}
                    >
                      {deletingId === metric.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};
